import { Star } from "lucide-react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { StepProgressBar } from "@/components/tracking/StepProgressBar";
import type { Booking, BookingStatus } from "@/types";

interface BookingCardProps {
  booking: Booking;
}

const statusConfig: Record<BookingStatus, { label: string; statusText: string; step: number }> = {
  pending_payment: { label: "Pending Payment", statusText: "Awaiting Payment", step: 1 },
  paid: { label: "Confirmed", statusText: "Confirmed", step: 1 },
  assigned: { label: "Finding Filmmaker", statusText: "Finding Filmmaker", step: 1 },
  en_route: { label: "En Route", statusText: "is On His Way", step: 2 },
  arrived: { label: "Arrived", statusText: "Has Arrived", step: 2 },
  in_progress: { label: "In Progress", statusText: "Filming in Progress", step: 3 },
  awaiting_finish_confirm: { label: "Awaiting Confirmation", statusText: "Awaiting Confirmation", step: 3 },
  done_waiting_upload: { label: "Uploading Content", statusText: "is Uploading Your Content", step: 4 },
  uploaded: { label: "Shooting Done", statusText: "Shooting Done", step: 4 },
  completed: { label: "Completed", statusText: "Completed", step: 4 },
  canceled: { label: "Canceled", statusText: "Canceled", step: 1 },
};

export default function BookingCard({ booking }: BookingCardProps) {
  const bookingId = 'BK-' + booking.id.substring(0, 8).toUpperCase();
  const status = statusConfig[booking.status];
  const currentStep = status.step;
  const totalSteps = 4;
  
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const day = date.getDate();
    const suffix = day === 1 || day === 21 || day === 31 ? 'st' : 
                   day === 2 || day === 22 ? 'nd' : 
                   day === 3 || day === 23 ? 'rd' : 'th';
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${month} ${day}${suffix}, ${year}`;
  };

  const filmmakerName = booking.filmmaker?.full_name || 'Filmmaker';
  const filmmakerRating = booking.filmmaker?.rating || 4.9;
  const filmmakerAvatar = booking.filmmaker?.avatar_url;

  // Calculate rating stars (4.9 = 4 full + 1 partial)
  const fullStars = Math.floor(filmmakerRating);
  const hasPartialStar = filmmakerRating % 1 >= 0.5;

  return (
    <Card className="p-5 bg-white rounded-xl shadow-sm">
      <div className="space-y-4">
        {/* Date */}
        <p className="text-sm text-gray-400">
          {booking.now_flag ? 'Today' : formatDate(booking.scheduled_at)}
        </p>

        {/* Session Name */}
        <h3 className="font-bold text-xl text-black">{booking.package?.name || 'The Glow Session'}</h3>

        {/* Status Text */}
        <p className="text-base text-black">
          {booking.filmmaker && booking.status !== 'assigned' ? `${filmmakerName} ${status.statusText}` : status.statusText}
        </p>

        {/* Progress Bar */}
        <StepProgressBar currentStep={currentStep} totalSteps={totalSteps} className="h-2" />

        {/* Step Count */}
        <p className="text-sm text-gray-400">
          Step {currentStep} of {totalSteps}
        </p>

        {/* Separator + Filmmaker Info - Only show after filmmaker accepts (en_route or later) */}
        {booking.filmmaker && booking.status !== 'assigned' && booking.status !== 'paid' && (
          <div className="border-t border-gray-200 pt-4 mt-2">
            {/* Filmmaker Info */}
            <div className="flex items-center gap-3 mb-4">
              <Avatar className="h-12 w-12">
                {filmmakerAvatar && <AvatarImage src={filmmakerAvatar} alt={filmmakerName} />}
                <AvatarFallback className="bg-gray-100 text-gray-600">
                  {filmmakerName.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="font-bold text-base text-black">{filmmakerName}</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex items-center gap-0.5">
                    {Array.from({ length: 5 }).map((_, index) => {
                      if (index < fullStars) {
                        return <Star key={index} className="h-4 w-4 fill-orange-500 text-orange-500" />;
                      } else if (index === fullStars && hasPartialStar) {
                        return (
                          <div key={index} className="relative h-4 w-4">
                            <Star className="h-4 w-4 fill-gray-300 text-gray-300 absolute" />
                            <Star className="h-4 w-4 fill-orange-500 text-orange-500 absolute" style={{ clipPath: 'inset(0 50% 0 0)' }} />
                          </div>
                        );
                      } else {
                        return <Star key={index} className="h-4 w-4 fill-gray-300 text-gray-300" />;
                      }
                    })}
                  </div>
                  <span className="text-sm text-black">{filmmakerRating.toFixed(1)}/5.0</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View Shooting Button */}
        <Link 
          to={`/track/${bookingId}`}
          className="block"
        >
          <Button className="w-full bg-gray-800 hover:bg-gray-700 text-white font-normal rounded-lg py-2.5">
            View Shooting
          </Button>
        </Link>
      </div>
    </Card>
  );
}